'use client';

import type { FC, ReactNode } from 'react';
import { CustomAccordion } from '@/app/components/shared/CustomAccordion';
import { TASK_SETTING_OPTION } from '@/app/constants';
import { cn } from '@/app/utils';
import {
  AmountOption,
  AspectRatioOptionSetting,
  DurationOptionSetting,
  GridCountOptionSetting,
  ResolutionOptionSetting,
  ScenarioOptionSetting,
  SwitchOptionSetting,
  type ToolSettingOptionProps,
  VoiceLanguageOptionSetting,
} from './ToolSettingsOptions';

interface ToolSettingsWithConfigProps {
  className?: string;
  includedOptions: TASK_SETTING_OPTION[];
  optionsClassName?: string[];
  optionClassName?: string;
  optionsWrapperClassName?: string;
  optionContainerClassName?: string;
  showOptionDivider?: boolean;
  useAccordion?: boolean;
  accordionTitle?: string;
  defaultExpanded?: boolean;
  modelVersionOption?: ReactNode;
  aspectRatioOption?: ToolSettingOptionProps<string>;
  resolutionOption?: ToolSettingOptionProps<string>;
  durationOption?: ToolSettingOptionProps<number>;
  gridCountOption?: ToolSettingOptionProps<number>;
  amountOption?: ToolSettingOptionProps<number>;
  customOptions?: Partial<Record<TASK_SETTING_OPTION, ReactNode>>;
  scenarioOption?: ToolSettingOptionProps<string>;
  voiceLanguageOption?: ToolSettingOptionProps<string>;
  subtitleOption?: ReactNode;
  retainOriginalAudioOption?: ToolSettingOptionProps<boolean>;
}

export const ToolSettingsWithConfig: FC<ToolSettingsWithConfigProps> = ({
  className,
  includedOptions,
  optionsClassName,
  optionClassName,
  optionsWrapperClassName,
  optionContainerClassName,
  showOptionDivider = true,
  useAccordion = false,
  accordionTitle = '',
  defaultExpanded = true,
  modelVersionOption,
  aspectRatioOption,
  resolutionOption,
  durationOption,
  gridCountOption,
  amountOption,
  customOptions,
  scenarioOption,
  voiceLanguageOption,
  subtitleOption,
  retainOriginalAudioOption,
}) => {
  const optionComponents: Partial<Record<TASK_SETTING_OPTION, ReactNode>> = {
    [TASK_SETTING_OPTION.Scenario]: scenarioOption ? (
      <ScenarioOptionSetting {...scenarioOption} className={optionClassName} />
    ) : null,
    [TASK_SETTING_OPTION.ExtendModalVersion]: modelVersionOption,
    [TASK_SETTING_OPTION.AspectRatio]: aspectRatioOption ? (
      <AspectRatioOptionSetting {...aspectRatioOption} className={optionClassName} />
    ) : null,
    [TASK_SETTING_OPTION.Resolution]: resolutionOption ? (
      <ResolutionOptionSetting {...resolutionOption} className={optionClassName} />
    ) : null,
    [TASK_SETTING_OPTION.Duration]: durationOption ? (
      <DurationOptionSetting {...durationOption} className={optionClassName} />
    ) : null,
    [TASK_SETTING_OPTION.GridCount]: gridCountOption ? (
      <GridCountOptionSetting {...gridCountOption} className={optionClassName} />
    ) : null,
    [TASK_SETTING_OPTION.VoiceLanguage]: voiceLanguageOption ? (
      <VoiceLanguageOptionSetting {...voiceLanguageOption} className={optionClassName} />
    ) : null,
    [TASK_SETTING_OPTION.Subtitle]: subtitleOption,
    [TASK_SETTING_OPTION.RetainOriginalAudio]: retainOriginalAudioOption ? (
      <SwitchOptionSetting {...retainOriginalAudioOption} className={optionClassName} />
    ) : null,
    [TASK_SETTING_OPTION.Amount]: amountOption ? <AmountOption {...amountOption} className={optionClassName} /> : null,
    ...customOptions,
  };

  const content = (
    <div className={cn('flex flex-col md:gap-3', optionsWrapperClassName)}>
      {includedOptions.map((option, index) => {
        const component = optionComponents[option];

        if (!component) {
          return null;
        }

        return (
          <div
            key={option}
            className={cn(
              showOptionDivider &&
                "after:my-3 after:block after:h-px after:w-full after:bg-system-white04 after:content-[''] last:after:hidden md:after:hidden",
              optionContainerClassName,
              optionsClassName?.[index],
            )}
          >
            {component}
          </div>
        );
      })}
    </div>
  );

  return (
    <div className={className}>
      {useAccordion ? (
        <CustomAccordion title={accordionTitle} defaultExpanded={defaultExpanded} isUseAccordion={useAccordion}>
          {content}
        </CustomAccordion>
      ) : (
        content
      )}
    </div>
  );
};
